import React, { useState } from 'react';
import { Button, FileUploader, Text, UploadCollection } from '@ui5/webcomponents-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import localforage from 'localforage';
import { toolOptions, productOptions, landTypeOptions, areaUnits, assistanceSource, assistanceType, animalsForFood, animalsForPets,
    vesselTypeOptions, materialUsedOptions, civilStatusOptions, cookingFacOptions, landOwnershipOptions, disasterImpactOptions, disasterTypeOptions,
    ecologicalSettingOptions, waterAvailabilityOptions, waterLocations, toiletSharingOptions, garbageDisposalOptions, toiletFacilities,
    waterSourceOptions, evacuationAreaOptions, experiencedDisasterOptions, externalHouseWallOptions, familyTypeOptions, fishpondTypeOptions,
    fishpondOwnershipOptions, waterTypeOptions, fisheriesOptions, floorMatOptions, foundationMaterialOptions, gableWallMaterialOptions,
    houseAffectedByDisasterOptions, houseBuiltBefore1990Options, houseConditionOptions, houseExtensionOptions, houseLocationOptions,
    ownershipOptions, houseShapeOptions, houseStructureOptions, internetProviderOptions, householdTypes, socialProtectionOptions, 
    mobileProviderOptions, roofingMatOptions, soilConditionOptions, sourceElectOptions, steelStandardOptions, wallSupportOptions, wallThicknessOptions,
    classOfWorkerOptions, contraceptiveOptions, dewormingStatusOptions, isEverPregnantOptions, genderOptions, healthInsuranceOptions,
    ifaIntakeOptions, ifaSourceOptions, lactationStatusOptions, migrationStatusOptions, pregnancyStatusOptions, prenatalCheckupOptions,
    civilRegisteredOptions, registeredVoterOptions, relationshipOptions, schoolingReasonsOptions, schoolingStatusOptions, seekingWorkOptions,
    sexOptions, educationalAttainmentOptions, soloParentOptions, feedingStatusOptions, skillsOptions, bilangPunoAniUnit, withSeniorIdOptions, isOFWOptions
} from '../models/selectionConstants.js';

const MassUploadManager = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });
  const [validationErrors, setValidationErrors] = useState([]);

  const LoadingOverlay = ({ message = "Uploading data. Please wait..." }) => {
    const overlayStyle = {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backdropFilter: "blur(3px)",
      zIndex: 9999,
    };

    const containerStyle = {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "20px 30px",
      boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "10px",
    };

    const spinnerStyle = {
      width: "40px",
      height: "40px",
      border: "4px solid #3498db",
      borderTop: "4px solid transparent",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    };

    const textStyle = {
      color: "#333",
      fontSize: "14px",
      fontFamily: "Arial, sans-serif",
    };

    return (
      <div style={overlayStyle}>
        <style>
          {`@keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }`}
        </style>
        <div style={containerStyle}>
          <div style={spinnerStyle}></div>
          <p style={textStyle}>{message}</p>
        </div>
      </div>
    );
  };

  const PopupMessage = ({ type = "success", message, onClose }) => {
    const overlayStyle = {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backdropFilter: "blur(3px)",
      zIndex: 9999,
    };

    const containerStyle = {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "24px 32px",
      boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      width: "300px",
    };

    const iconStyle = {
      fontSize: "40px",
      marginBottom: "10px",
      color: type === "success" ? "#27ae60" : "#e74c3c",
    };

    const messageStyle = {
      color: "#333",
      fontSize: "15px",
      marginBottom: "20px",
      fontFamily: "Arial, sans-serif",
    };

    const buttonStyle = {
      backgroundColor: "#007bff",
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "8px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "bold",
    };

    return (
      <div style={overlayStyle}>
        <div style={containerStyle}>
          <div style={iconStyle}>
            {type === "success" ? "✓" : "✕"}
          </div>
          <p style={messageStyle}>{message}</p>
          <button style={buttonStyle} onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().split('T')[0];
  };

  const safeJsonParse = (jsonString, defaultValue = {}) => {
    if (!jsonString || typeof jsonString !== 'string') return defaultValue;
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.warn("Failed to parse JSON string:", jsonString, e);
      return defaultValue;
    }
  };

  const splitCommaSeparated = (str) => {
    if (!str || typeof str !== 'string') return [];
    return str.split(',').map(item => item.trim()).filter(item => item !== '');
  };

  const applianceItems = [
    "Refrigerator", 
    "Washing Machine", 
    "Electric Fan",
    "Air Conditioner", 
    "Rice Cooker", 
    "Microwave Oven", 
    "Gas Stove",
    "Electric Stove",
    "Blender",
    "Water Dispenser",
    "Vacuum Cleaner",
    "Sewing Machine",
    "Flat Iron",
    "Oven Toaster",
    "Others"
  ];
  const ictDeviceItems = [
    "Radio/Tape recorder/Stereo", 
    "Television (TV)", 
    "VCR/Karaoke/Videoke", 
    "Video Games", 
    "Computer/Laptop", 
    "Printer", 
    "CD/VCD/DVD Player", 
    "Telephone",
    "Mobile Phone",
    "Tablet"
  ];
  const vehicleItems = [
    "Car", 
    "Motorcycle",
    "Bicycle",
    "Tricycle", 
    "Boat",
    "Truck",
    "Others" 
  ];
  const HooksAndLine = [
    "Simple and hand line (Kawil)",
    "Multiple hand line (birabira, pangawil, pangundak, panlagaw, ug-ug)",
    "Bottom set long line (Kitangna pang-ilalim ng dagat)",
    "Drift long line (kitang na paanod sa ibabaw ng dagat)",
    "Troll line (Pahila, Salabay)",
    "Squid jigs (Pamusit, Saranggat)"
  ];
  const SeineNets = [
    "Beach seine (pukot)",
    "Fry dozer or gatherer (salap)",
    "Cast Net (Dala, bitak)",
    "Man push nets (sudsod, tunod, sagap)",
    "Scoop nets (sirok, sigma)"
  ];
  const FallingGear = ["Filter nets (Lumpot)"];
  const LiftNets = [
    "Crab lift nets (Bintol ng Alimango)",
    "Fish lift nets (Basnig/Bagnet)",
    "New Look o Zapra",
    "Shrimp lift nets (Bintol ng Hipon/Sugpo)",
    "Lever nets (Salambaw)"
  ];
  const PotsAndTraps = [
    "Fish pots (Bubo ng Isda)",
    "Crab pots (Bubo ng Alimango)",
    "Squid pots (Bubo ng pusit)",
    "Fyke nets (Skylab/Palos)"
  ];
  const GillNets = [
    "Surface set gill net",
    "Drift gill net (panting paanod)",
    "Bottom set gill net (panting palubog)",
    "Trammel net (Transmallo, Pangubiw)",
    "Encircling gill net",
    "Shrimp gill net (Galagdag, Pamasayan, Lait)"
  ];
  const MiscellaneousFishingGear = [
    "Spear (Pana)",
    "Octopus/squid luring device (Pangati/Pamusit)",
    "Gaff hook (Ganso, Panikwat)"
  ];
  const foodSecurity = ['FoodSecurity_H1', 'FoodSecurity_H2', 'FoodSecurity_H3', 'FoodSecurity_H4', 'FoodSecurity_H5', 'FoodSecurity_H6', 'FoodSecurity_H7', 'FoodSecurity_H8', 'FoodSecurity_H9', 'FoodSecurity_H10', 'FoodSecurity_H11', 'FoodSecurity_H12'];
  const peaceAndOrder = ['PeaceAndOrder_J1', 'PeaceAndOrder_J2', 'PeaceAndOrder_J3_Province', 'PeaceAndOrder_J3_CityMunicipality', 'PeaceAndOrder_J4'];
  const socialProtection = ['SocialProtection_I1', 'SocialProtection_I2'];

  // validation functions
  const validateProvince = async (provinceInput, field, headId, memberId) => {
    const provinceData = await localforage.getItem('location_province');
    const provincesList = provinceData.data;
    for (const province of provincesList) {
      if ((province.province_name || '').toUpperCase() === provinceInput) return province.province_id;
    }
    setValidationErrors((prev) => [...prev, `Cannot find ${headId}'s ${memberId ? `and ${memberId}'s` : ''} ${field} named ${provinceInput}`])
    return null
  }

  const validateCityMunicipality = async (cityMunicipalityInput, provinceId, field, headId, memberId) => {
    const cityMunicipalityData = await localforage.getItem('location_municipality');
    const cityMunicipalityList = cityMunicipalityData.data.find(item => item.province_id === provinceId);
    if (cityMunicipalityList) {
      for (const cityMunicipality of cityMunicipalityList.municipalities) {
        if ((cityMunicipality.municipality_name || '').toUpperCase() === cityMunicipalityInput) return cityMunicipality.municipality_id;
      }
    }
    setValidationErrors((prev) => [...prev, `Cannot find ${headId}'s ${memberId ? `and ${memberId}'s` : ''} ${field} named ${cityMunicipalityInput}`]);
    return null
  }

  const validateBarangay = async (barangayInput, cityMunicipalityId, field, headId, memberId) => {
    const barangayData = await localforage.getItem('location_barangay');
    const barangaysList = barangayData.data.find(item => item.municipality_id === cityMunicipalityId);
    if (barangaysList) {
      for (const barangay of barangaysList.barangays) {
        if ((barangay.barangay_name || '').toUpperCase() === barangayInput) return barangay.barangay_id;
      }
    }
    setValidationErrors((prev) => [...prev, `Cannot find ${headId}'s ${memberId ? `and ${memberId}'s` : ''} ${field} named ${barangayInput}`]);
    return null
  }

  const validateStatus = (statusInput, headId, memberId) => {
    const validStatus = ['A - Active', 'D - Died', 'T - Transferred']
    for (const status of validStatus) {
      if ((statusInput || '').toUpperCase() === (status || '').toUpperCase()) return statusInput;
    }
    setValidationErrors((prev) => [...prev, `${headId}'s ${memberId ? `, ${memberId}'s` : ''} status named ${statusInput} is invalid`]);
    return null;
  }

  const validateValue = (headId, memberId, selection, input, fieldName) => {
    if (input === undefined || input === '') return '';
    if (input) {
      const values = selection.map(item => item.value);
      for (const value of values) {
        if (Number(input) === Number(value)) {
          return value 
        }
      }
    }
    setValidationErrors((prev) => [...prev, `${headId}'s ${memberId ? `, ${memberId}'s` : ''} ${fieldName} = ${input} is invalid`]);
    return '';
  }

  const validateString = (headId, memberId, selection, input, fieldName) => {
    if (input === undefined || input === '') return '';
    if (input) {
      const labels = selection.map(item => item.label);
      for (const label of labels) {
        if (input.toUpperCase() === label.toUpperCase()) {
          return label
        }
      }
    }
    setValidationErrors((prev) => [...prev, `${headId}'s ${memberId ? `, ${memberId}'s` : ''} ${fieldName} = ${input} is invalid`]);
    return '';
  }

  const validateQuantity = (tempId, gears, gear) => {
    if (gears[gear] !== undefined && gears[gear] !== null) {
      const value = String(gears[gear]).trim();
      if (value !== '' && !isNaN(value) && Number(value) >= 0 && Number.isInteger(Number(value))) {
        return Number(value);
      } else if (value !== '') {
        setValidationErrors((prev) => [...prev, `${tempId}'s ${gear} field has an invalid amount.`]);
      }
    }
    return 0
  }

  const validateAssetQuantity = (tempId, assets, assetType, item) => {
    const rawValue = assets[item];
    const value = rawValue !== undefined && rawValue !== null ? String(rawValue).trim() : '';
    if (value !== '' && !isNaN(value) && Number(value) >= 1 && Number.isInteger(Number(value))) {
      return {
        asset_type: assetType,
        asset_name: item,
        quantity: Number(value)
      };
    }
    if (value !== '' && Number(value) !== 0) {
      setValidationErrors((prev) => [...prev, `${tempId}'s ${assets[item]} ${item} of type ${assetType} value of ${assets[item]} is not valid.`])
    }
  }

  const isValidYesNoInput = (tempId, input, field) => {
    if (input === undefined) {
      return false
    }
    if (['Oo', 'Hindi'].includes(input)) {
      return true
    }
    setValidationErrors((prev) => [...prev, `${tempId}'s ${field} = ${input} is invalid.`]);
    return false
  }

  const validateFloat = (tempId, memberId, input, fieldName) => {
    if (input === undefined || input === null || input === '') {
      return '';
    }
    const num = parseFloat(input);
    if (!isNaN(num) && isFinite(num)) {
      return num;
    } else {
      setValidationErrors((prev) => [...prev, `${tempId}'s ${memberId ? `, ${memberId}'s` : ''} ${fieldName} = ${input} is invalid`]);
    }
  }

  const validateInteger = (tempId, memberId, input, fieldName) => {
    if (input === undefined || input === null || input === '') {
      return '';
    }
    const num = parseFloat(input);
    if (!isNaN(num) && Number.isInteger(num)) {
      return num;
    } else {
      setValidationErrors((prev) => [...prev, `${tempId}'s ${memberId ? `, ${memberId}'s` : ''} ${fieldName} = ${input} is invalid`]);
    }
  }

  const handleDownloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    const includeDummyData = true;

    const sheets = {
      'family_head': [
        ['TempHeadID', 'SurveyYear', 'FirstName', 'MiddleName', 'LastName', 'Suffix', 'Birthdate', 'BirthPlace', 'Citizenship', 'Sex', 
          'Gender', 'CivilStatus', 'HouseholdType', 'FamilyType', 'HeadID', 'Status', 'HouseNo', 'Street', 'PurokSitio', 'Barangay', 
          'CityMunicipality', 'Province', 'Latitude', 'Longitude', 'NumberOfFamilyMembers', 'YearMoved', 'PrevLocationProvince', 'PrevLocationCityMunicipality', 'InternetProvider', 'InternetProviderOther'],
        ...(includeDummyData ? [
          ['H1', 2025, 'Jose', 'Mercado', 'Rizal', 'Jr', '1861-06-19', 'Calamba, Laguna', 'Filipino', 'Male', '', 'S - Single', 'Family', 'Main Family (Owner)', '', 'A - Active', '16', 'Molave', 'Tubigan', 'Bucal', 'Calamba City', 'Laguna', 13.8211, 121.13, 2, '2003', 'Agusan del Norte', 'Carmen', 'Fixed Wired', ''],
          ['H2', 2025, 'Maria', 'Mestiza', 'Mendiola', '', '1953-06-19', 'Nagcarlan, Laguna', 'Filipino', 'Female', '', 'M - Married', 'Family', 'Main Family (Owner)', '', 'A - Active', '9023', 'Halcon', 'Palayan', 'Sabang', 'Nagcarlan', 'Laguna', 13.2390, 122.242, 3, '2001', 'Batangas', 'Ibaan', 'Fixed Wireless', ''],
          ['H3', 2025, 'Jonathan', 'Lorenzo', 'Lapid', '', '1992-02-28', 'Ibaan, Batangas', 'Filipino', 'Male', '', 'S - Single', 'Family', 'Main Family (Owner)', '', 'A - Active', '532', 'Rosario', 'Ibabang Bukid', 'Pinagkawitan', 'Lipa City', 'Batangas', 14.9341, 123.232, 1, '1995', 'Sorsogon', 'Sorsogon City', 'Mobile Broadband Network', ''],
        ] : [])
      ],
      'contact_number': [
        ['TempHeadID', 'MobileProvider1', 'MobileProvider2', 'MobileProvider3', 'ContactNumber1', 'ContactNumber2', 'ContactNumber3'],
        ...(includeDummyData ? [
          ['H1', 'Globe', '', '', '09171234567', '', ''],
          ['H2', 'Smart', 'DITO', '', '09191234567', '09911234567', ''],
          ['H3', '', 'Sun Cellular', "TNT (Talk 'N Text)", '', '09177654321', '09197654321'],
        ] : [])
      ],
      'family_members': [
        ['TempHeadID', 'TempMemberID', 'FirstName', 'MiddleName', 'LastName', 'Suffix', 'Relationship', 'Status', 'CauseOfDeath', 'Sex', 'Gender', 
          'Birthdate', 'CivilStatus', 'BirthPlace', 'BirthProvince', 'BirthMunicipality', 'BirthBarangay', 'WithSeniorID', 'SeniorCitizenID', 'Citizenship', 'Religion', 'Ethnicity', 'RegisteredVoter', 
          'CivilRegistered', 'MigrationStatus', 'SeekingWork', 'ClassOfWorker', 'IsOFW', 'CountryOfWork', 'MainIncomeSource', 'OtherIncomeSource', 'MonthlyIncome', 'Skills', 'Organizations', 'SchoolingStatus', 'SchoolName', 'Degree', 'SchoolingReasons', 
          'EducationalAttainment', 'SoloParentId', 'SoloParentIdNo'],
        ...(includeDummyData ? [
          ['H1', 'M1', 'Jose', 'Mercado', 'Rizal', 'Jr', '9 - Head of the Family', 'A - Active', '', 'M - Male', '', 
            '1861-06-19', 'S - Single', 'Calamba, Laguna', 'Laguna', 'Calamba City', 'Bucal', '1 Yes', '32423402' ,'Philippines', 'Catholic', 'Tagalog', 'Yes - Registered here', 
            'Yes - Registered', 'NM - Non-migrant (>5 years)', '1 - Actively seeking', '4-Self-employed', '2 - No', '', 'FARMING', 'LENDING', 30000, 'Farming, Singing', 'Samahan ng mga Calambeno, KKK', '1 Currently Studying', 'Ateneo de Manila University', 'Doctor of Medicine', '',
            'P–Postgraduate', '3 Not Applicable', ''],
          ['H1', 'M2', 'Carmelita', 'Mercado', 'Rizal', '', "10 - Housemaid", 'A - Active', '', 'F - Female', 'L - Lesbian',
            '1902-04-23', 'NM - Not Married/Common Law', 'Calamba, Laguna', 'Laguna', 'Calamba City', 'Halang', '2 No', '', 'Philippines', 'Catholic', 'Bisaya', 'Yes - Registered here',
            'No - Not registered', 'T - Transient', '3 - Employed', '5-Employer', '2 - No', '', 'House Keeping', 'Marketing', '12000', 'Cleaning, Child caring', 'Philippine Housekeepers Association (PHA)', '2 Not Studying', '', '', '',
            'HS2', '3 Not Applicable', ''],
          ['H2', 'M1', 'Maria', 'Mestiza', 'Mendiola', '', '9 - Head of the Family', 'A - Active', '', 'F - Female', '', 
            '1989-09-17', 'M - Married', 'Cabanatuan City, Nueva Ecija', 'Nueva Ecija', 'Cabanatuan City', 'Bitas', '2 No', '', 'Philippines', 'Catholic', 'Ilocano', 'Yes - Registered here', 
            'Yes - Registered', 'M - Migrant (>=6 months)', '3 - Employed', '3-Govt/Govt Corp', '2 - No', '', 'Canteen Manager', 'Baking', '50000', 'Cooking, Pastry Making', '', '2 Not Studying', '', 'BS Food Science', '',
            'College Grad', '2 No', ''],
          ['H2', 'M2', 'Carlo', 'Jose', 'Mendiola', '', '16 - Spouse', 'A - Active', '', 'M - Male', '',
            '1990-01-12', 'M - Married', 'Lingayen, Pangasinan', 'Pangasinan', 'Lingayen', 'Lasip', '2 No', '', 'Philippines', 'Catholic', 'Ilocano', 'No - Registered elsewhere',
            'Yes - Registered', 'NM - Non-migrant (>5 years)', '3 - Employed', '2-Private Establishment', '1 - Yes', 'Germany', 'Mechanical Welding', '', '100000', 'Welding, Dancing, Horse Racing', 'Overseas Filipino Workers in Europe', '2 Not Studying', '', 'BS Mechanical Engineering', '',
            'College Grad', '2 No', ''],
          ['H2', 'M3', 'Jessa', 'Mestiza', 'Mendiola', '', '4 - Child', 'A - Active', '', 'F - Female', '', 
            '2024-10-10', 'S - Single', 'Lingayen, Pangasinan', 'Pangasinan', 'Lingayen', 'Lasip', '2 No', '', 'Philippines', 'Catholic', 'Tagalog', 'NA - Not yet voter',
            'No - Not registered', 'NM - Non-migrant (>5 years)', '', '8-Not Applicable', '2 - No', '', '', '', '', '', '', '2 Not Studying', '', '', '11 Too Young',
            '', '2 No', ''],
          ['H3', 'M1', 'Jonathan', 'Lorenzo', 'Lapid', '', '9 - Head of the Family', 'A - Active', '', 'M - Male', '',
            '1982-12-05', 'LS - Legally Separated', 'Ibaan, Batangas', 'Batangas', 'Ibaan', 'Poblacion', '3 Not Applicable', '', 'Philippines', 'Catholic', 'Tagalog', 'Yes - Registered here',
            'Yes - Registered', 'NM - Non-migrant (>5 years)', '3 - Employed', '7-Unpaid Family Farm/Biz', '2 - No', '', 'Fishing', '', '20000', 'Carpentry, Fishpond Caring', '', '2 Not Studying', '', '', '',
            'JHS Grad', '1 Yes', '12876234'],
          ['H3', 'M2', 'Joanne', 'Comia', 'Lapid', '', '4 - Child', 'A - Active', '', 'F - Female', '',
            '2003-07-12', 'S - Single', 'Ibaan, Batangas', 'Batangas', 'Ibaan', 'Poblacion', '3 Not Applicable', '', 'Philippines', 'Catholic', 'Tagalog', 'Yes - Registered here',
            'Yes - Registered', 'NM - Non-migrant (>5 years)', '2 - Not actively seeking', '8-Not Applicable', '2 - No', '', '', '', '', 'Crocheting, Singing', 'SK Youth, Catholic Teens of Batangas', '1 Currently Studying', 'Batangas State University', 'BS Biology', '',
            'C4', '3 Not Applicable', '' ]
        ] : [])
      ],
      'socioeconomic_assets': [
        ['TempHeadID', ...applianceItems, ...ictDeviceItems, ...vehicleItems],
        ...(includeDummyData ? [
          ['H1', 
            1, 1, 0, 0, 1, 2, 1, 0, 0, 0, 0, 1, 1, 0, 2, 
            0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 
            0, 0, 0, 0, 0, 1, 0
          ],
          ['H2', 
            0, 1, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 0, 1, 
            0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 
            1, 0, 0, 0, 1, 0, 0
          ],
          ['H3', 
            1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 
            0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 
            0, 0, 0, 0, 1, 0, 1
          ],
        ] : [])
      ],
      'agriculture_land': [
        ['HeadID', 'Lawak', 'LawakUnit', 'Probinsya', 'Munisipyo', 'Barangay', 'Kalagayan', 'MayAri'],
        ...(includeDummyData ? [
          ['H1', '1.2', 'hectares', 'Batangas', 'Ibaan', 'Matala', 2, 'Kapatid'],
          ['H1', '0.5', 'hectares', 'Batangas', 'Lipa City', 'Quezon', 3, 'Clara Perez'],
          ['H2', '2.0', 'sqm', 'Nueva Ecija', 'Cabanatuan City', 'San Isidro', 4, 'Bonifacio'],
        ] : [])
      ],
      'agriculture_products': [
        ['HeadID', 'Area', 'Pananim', 'UriLupa', 'Ektarya', 'BilangPuno', 'Unit', 'BilangPunoUnit', 'Probinsya', 'Munisipyo', 'Barangay'],
        ...(includeDummyData ? [
          ['H1', 12, 1, 2, 20, 100, 'sqm', 'Puno', 'Batangas', 'Ibaan', 'Matala'],
          ['H1', 10, 2, 4, 10.5, 200, 'sqm', 'Kg', 'Batangas', 'Ibaan', 'Matala'],
          ['H1', 4.5, 3, 5, 3.4, 50, 'hectares', 'Sako/Kaban', 'Batangas', 'Lipa City', 'Quezon'],
        ] : [])
      ],
      'agriculture_machineries': [
        ['HeadID', 'Uri', 'Bilang'],
        ...(includeDummyData ? [
          ['H1', '15', '1'],
          ['H1', '16', '2'],
          ['H1', '24', '1'],
        ] : [])
      ],
      'animals_raised': [
        ['HeadID', 'Uri', 'Bilang'],
        ...(includeDummyData ? [
          ['H1', '1', '5'],
          ['H1', '2', '20'],
          ['H3', '18', '3'],
        ] : [])
      ],
      'pet_animals': [
        ['HeadID', 'Uri', 'Bilang'],
        ...(includeDummyData ? [
          ['H1', '1', '2'],
          ['H2', '1', '1'],
          ['H2', '3', '2'],
          ['H2', '5', '2'],
          ['H3', '9', '3'],
          ['H3', '1', '3'],
          ['H3', '16', '10'],
          ['H3', '17', '1'],
        ] : [])
      ],
      'fisheries': [
        ['HeadID', 'Isda', 'Bilang', 'Kalagayan', 'TubigUri', 'FishpondType', 'Sukat', 'Unit', 'Probinsya', 'Munisipyo', 'Barangay'],
        ...(includeDummyData ? [
          ['H3', '3', '200', '1', '2', '2', '100', '20', 'Laguna', 'Bay', 'Tagumpay'],
          ['H3', '4', '200', '2', '3', '1', '150', '40', 'Rizal', 'Tanay', 'Tinucan'],
        ] : [])
      ],
      'fisheries_boats': [
        ['HeadID', 'Uri', 'Bilang', 'CommercialCount', 'MunicipalCount', 'MaterialUsed'],
        ...(includeDummyData ? [
          ['H3', '1', '1', '1', '0', '3'],
          ['H3', '2', '1', '0', '1', '2'],
        ] : [])
      ],
      'nipa_production': [
        ['HeadID', 'Uri', 'Bilang', 'Probinsya', 'Munisipyo', 'Barangay'],
        ...(includeDummyData ? [
          ['H1', 'Nipa Hut', '1', 'Laguna', 'Calamba City', 'Canlubang'],
          ['H3', 'Pawid', '1', 'Laguna', 'Bay', 'Dila'],
          ['H3', 'Nipa Hut', '1', 'Rizal', 'Tanay', 'Laiban'],
        ] : [])
      ],
      'fisheries_gear': [
        ['HeadID', ...HooksAndLine, ...SeineNets, ...FallingGear, ...LiftNets, ...PotsAndTraps, ...GillNets, ...MiscellaneousFishingGear],
        ...(includeDummyData ? [
          ['H3', 0, 0, 0, 2, 0, 0, 1, 0, 3, 0, 0, 0, 0, 0, 1, 1, 0, 0, 1, 0, 2, 0, 0, 0, 2, 1, 1, 0, 1, 1],
        ] : [])
      ],
      'environment_sanitation': [
        ['HeadID', 'SelectedGeneralWaterSource', 'GeneralWaterLocation', 'IsGeneralWaterAvailable', 'SelectedDrinkingWaterSource', 'DrinkingWaterLocation', 'IsDrinkingWaterAvailable', 'SelectedToiletFacility', 'ToiletLocation', 'ToiletSharing', 'SelectedGarbageDisposal'],
        ...(includeDummyData ? [
          ['H1', 'Tubed/Piped Shallow Well (<25m), Barangay Water System', 'In Own Dwelling', 'Yes', 'Mineral/Bottled Water', 'Elsewhere', 'Yes', 'Pour/Flush to Septic Tank', 'In Own Dwelling', 'Household members only', 'Collected by City/Municipality'],
          ['H2', 'Barangay Water System', 'In Own Dwelling', 'No', 'Barangay Water System', 'In Own Dwelling', 'Yes', 'Pour/Flush to Septic Tank', 'In Own Dwelling', 'Household members only', 'Practiced Recycling/Reusing'],
          ['H3', 'Public Tap', 'In Own Yard/Plot', 'Yes', 'Tanker Truck', 'In Own Dwelling', 'No', 'Pit Latrine without Slab/Open Pit', 'In Own Dwelling', 'Other known household', 'Dumped in Compost Pit'],
        ] : [])
      ],
      'member_health_nutrition': [
        ['HeadID', 'MemberID', 'Disease', 'Disability', 'PWDId', 'HealthInsurance', 'VaccinesReceived', 'IFAIntake', 'IFASource',
          'ContraceptivesUsed', 'EverPregnant', 'LactationStatus', 'PregnancyStatus', 'PrenatalCheckup', 'TotalPregnancies',
          'TotalMiscarriages', 'TotalStillBirths', 'TotalLivingChildren', 'BCG', 'HepatitisB', 'IPV', 'MMR', 'OPV', 'Pentavalent', 'PCV', 
          'OtherVaccines', 'DewormingStatus', 'DewormedDate', 'VitaminA', 'Iron', 'MNP', 'IFR', 'IodizedSalt', 'MicronutrientStatus', 'MicronutrientDate',
          'BreastfeedingStatus', 'EBF_1', 'EBF2', 'EBF3', 'EBF_4', 'EBF_5', 'EBF_6', 'BreastFeedingDate', 'FeedingStatus'
        ],
        ...(includeDummyData ? [
          ['H1', 'M1', 'Hypertension', 'Blind', '12395324', '4 – Cooperative/LGU Health Card', '', '', '',
            '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', ''],
          ['H1', 'M2', '', '', '', '6 - None', '', '', '',
            '', 'Yes', 'Not lactating', 'Not pregnant', 'Does not go to prenatal check-ups', '2',
            '0', '2', '2', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', ''],
          ['H2', 'M1', '', '', '', '1 – Social Security System (SSS)', '', '', '',
            'Condom', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', ''],
          ['H2', 'M2', '', '', '', '2 – Government Service Insurance System (GSIS)', '', '', '',
            'Calendar-based Method', 'Yes', 'Not lactating', 'Currently Pregnant', 'Goes to prenatal check-ups', '2',
            '0', '1', '1', '1', '', '', '', '', '', '', '',
            '' , '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', ''],
          ['H2', 'M3', '', '', '', '', '', '', '',
            '', '', '', '', '', '',
            '', '', '', 1, 1, 1, 1, 1, 1, 1,
            '', '1 Dewormed', '2025-05-12', 1, 1, 0, 1, 1, '', '', 
            '', '1', '1', '1', 0, 0, 0, '2024-12-22', '4 Breastfeed + Complementary Food'],
          ['H3', 'M1', '', '', '', '3 – Philhealth', '', '', '',
            'Condom', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '', 
            '', '', '', '', '', '', '', '', ''],
          ['H3', 'M2', '', '', '', '5 – Dependent', '', 'Weekly', 'School',
            '', 'No', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '',
            '', '', '', '', '', '', '', '', '', '', 
            '', '', '', '', '', '', '', '', '']
        ] : [])
      ],
      'housing_characteristics' : [
        ['HeadID', 'LotOwnership', 'LotOwnershipOther', 'HouseOwnership', 'HouseOwnershipOther', 'HouseOwnershipType', 
          'HouseStructure', 'HouseStructureOther', 'HouseStructureType', 'ExternalHouseWall', 'RoofingMaterial', 
          'FlooringMaterial', 'SourceElectricity', 'CookingFacility'],
        ...(includeDummyData ? [ 
          ['H1', '1', '', '1', '', '', '2', '', '', '3', '5', '2', '1', '3'],
          ['H2', '2', '', '2', '', '', '1', '', '', '4', '6', '2', '1', '4'],
          ['H3', '5', '', '5', '', '', '6', '', '', '2', '1', '99', '2', '5']
        ] : [])
      ],
      'disaster_rrm' : [
        ['HeadID', 'HouseLocation', 'EcologicalSetting' , 'BuildingPermit', 'HouseBuiltBefore1990', 'HouseAffectedByDisaster', 
          'HouseShape', 'HouseExtension', 'WallThickness', 'SteelStandard', 'WallSupport', 'GabbleWallMaterial', 'FoundationMaterial', 
          'SoilCondition', 'HouseCondition', 'ExperiencedDisaster', 'DisasterType', 'EvacuationArea', 'DisasterImpact', 'AssistanceType', 'AssistanceSource'],
        ...(includeDummyData ? [
          ['H1', '3', '2', '2', '3', '1', '2', '2', '1', '2', '2', '3', '3', '1', '1', 'Oo', '1', '9', '5', '1', '4'],
          ['H2', '1', '2', '1', '2', '2', '2', '2', '1', '2', '2', '3', '2', '2', '3', 'Hindi', '1', '5', '5', '3', '2'],
          ['H3', '2', '3', '2', '1', '3', '2', '3', '2', '3', '1', '2', '1', '3', '3', 'Oo', '2', '7', '3', '2', '7'],
        ] : [])
      ],
      'last_page_data': [
        ['HeadID', ...foodSecurity, ...socialProtection, ...peaceAndOrder],
        ...(includeDummyData ? [
          ['H1', 'Hindi', 'Hindi', 'Oo', 'Oo', 'Hindi', 'Hindi', 'Hindi', 'Oo', 'Oo', 'Sambong', 'Oo', 'Malunggay, Okra, Sitaw, Kamatis', 'Social Pension for Senior Citizens, Others', 'Employment/Livelihood Assistance', 'Hindi', '', '', '', ''],
          ['H2', 'Hindi', 'Hindi', 'Hindi', 'Hindi', 'Hindi', 'Hindi', 'Hindi', 'Hindi', 'Oo', 'Lagundi', 'Hindi', '', 'Education Assistance Programs', '', 'Hindi', '', '', '', ''],
          ['H3', 'Oo', 'Oo', 'Oo', 'Oo', 'Hindi', 'Oo', 'Hindi', 'Oo', 'Hindi', '', 'Oo', 'Malunggay', '4Ps', 'Employment/Livelihood Assistance', 'Oo', 'Pagnanakaw', 'Cavite', 'Tanza', 'Ligtas'],
        ] : [])
      ]
    };

    Object.entries(sheets).forEach(([name, data]) => {
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, ws, name);
    });
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'BMIS_MassUpload_Template_V3.xlsx');
  };

  const transformToPayloads = async (data) => {
    // Group all rows by TempHeadID
    const heads = data.family_head || [];
    if (!heads.length) {
      alert('No family_head data found.');
      return [];
    }

    // Helper: group rows by TempHeadID
    const groupByTempHeadID = (sheet) => {
      const map = {};
      (sheet || []).forEach(row => {
        const key = row.TempHeadID || row.HeadID || '';
        if (!key) return;
        if (!map[key]) map[key] = [];
        map[key].push(row);
      });
      return map;
    };

    const contactMap = groupByTempHeadID(data.contact_number);
    const assetsMap = groupByTempHeadID(data.socioeconomic_assets);
    const membersMap = groupByTempHeadID(data.family_members);
    const envMap = groupByTempHeadID(data.environment_sanitation);
    const lastPageMap = groupByTempHeadID(data.last_page_data);

    // Other sheets (optional, fallback to HeadID if TempHeadID not present)
    const agriLandMap = groupByTempHeadID(data.agriculture_land);
    const agriProdMap = groupByTempHeadID(data.agriculture_products);
    const agriMachMap = groupByTempHeadID(data.agriculture_machineries);
    const animalsMap = groupByTempHeadID(data.animals_raised);
    const petsMap = groupByTempHeadID(data.pet_animals);
    const fisheriesMap = groupByTempHeadID(data.fisheries);
    const boatsMap = groupByTempHeadID(data.fisheries_boats);
    const nipaMap = groupByTempHeadID(data.nipa_production);
    const gearMap = groupByTempHeadID(data.fisheries_gear);

    // Addtional sheets
    const disaster_risk_reduction_management = groupByTempHeadID(data.disaster_rrm);
    const member_health_nutrition = groupByTempHeadID(data.member_health_nutrition);
    const housing_characteristics = groupByTempHeadID(data.housing_characteristics); 

    // Build one payload per TempHeadID
    // try {
      const results = await Promise.all(heads.map(async head =>  {
        const tempId = head.TempHeadID || '';
        const contact = (contactMap[tempId] && contactMap[tempId][0]) || {};
        const assets = (assetsMap[tempId] && assetsMap[tempId][0]) || {};
        const env = (envMap[tempId] && envMap[tempId][0]) || {};
        const lastPage = (lastPageMap[tempId] && lastPageMap[tempId][0]) || {};
        const gears = (gearMap[tempId] && gearMap[tempId][0]) || {};

        const drrm = (disaster_risk_reduction_management[tempId] && disaster_risk_reduction_management[tempId][0]) || {};
        const mhn = (member_health_nutrition[tempId] && member_health_nutrition[tempId]) || {};
        const hc = (housing_characteristics[tempId] && housing_characteristics[tempId][0]) || {};

        const provinceId = await validateProvince((head.Province || '').toUpperCase(), 'Province', tempId, null);
        const cityMunicipalityId = provinceId ? await validateCityMunicipality((head.CityMunicipality || '').toUpperCase(), provinceId, 'CityMunicipality', tempId, null) : null;
        const barangayId = cityMunicipalityId ? await validateBarangay((head.Barangay || '').toUpperCase(), cityMunicipalityId, 'Barangay', tempId, null) : null

        const prevLocationProvince = head.PrevLocationProvince.trim() !== '' ? await validateProvince((head.PrevLocationProvince || '').toUpperCase(), 'Previous Location Province', tempId, null) : null;
        const prevLocationCityMunicipality = prevLocationProvince ? await validateCityMunicipality((head.PrevLocationCityMunicipality || '').toUpperCase(), prevLocationProvince, 'Previous Location City Municipality', tempId, null) : null;
        const J3LocationProvince = lastPage.PeaceAndOrder_J3_Province.trim() !== '' ? await validateProvince((lastPage.PeaceAndOrder_J3_Province || '').toUpperCase(), 'Peace And Order J3 Province', tempId, null) : null;
        const J3LocationCityMunicipality = J3LocationProvince ? await validateCityMunicipality((lastPage.PeaceAndOrder_J3_CityMunicipality || '').toUpperCase(), J3LocationProvince, 'Peace And Order J3 City Municipality', tempId, null) : null;

        // Build householdAppliances, ictDevices, vehicles from columns
        const buildAssetObject = (items, row) => {
          const obj = {};
          items.forEach((label, index) => {
            const val = row?.[label];
            if (val !== undefined && val !== null && val !== '' && !isNaN(val) && Number(val) > 0) {
              obj[index] = Number(val);
            }
          });
          return obj;
        };

        return {
        agriculturalMachineries: (agriMachMap[tempId] || []).map((item, index) => ({
          tool: validateValue(tempId, null, toolOptions, item.Uri, `Agricultural Machinery ${index + 1}`) || '',
          quantity: validateInteger(tempId, null, item.Bilang, `Quantity of Agricultural Machinery ${index + 1}`)
        })),
        agriculturalProducts: await Promise.all((agriProdMap[tempId] || []).map(async (item, index) => {
          const provinceId = await validateProvince((item.Probinsya || '').toUpperCase(), 'Province of Agricultural Products', tempId, null);
          const cityMunicipalityId = provinceId ? await validateCityMunicipality((item.Munisipyo || '').toUpperCase(), provinceId, 'Municipality of Agricultural Products', tempId, null) : null;
          const barangayId = cityMunicipalityId ? await validateBarangay((item.Barangay || '').toUpperCase(), cityMunicipalityId, 'Barangay of Agricultural Products', tempId, null) : null;
          return ({
            areaInput: validateFloat(tempId, null, item.Area, `Area of Agricultural Product ${index + 1}`),
            pananim: validateValue(tempId, null, productOptions, item.Pananim, `Pananim of Agricultural Products ${index + 1}`) || '',
            uriLupa: validateValue(tempId, null, landTypeOptions, item.UriLupa, `Uri Lupa of Agricultural Products ${index + 1}`) || '',
            ektarya: validateFloat(tempId, null, item.Ektarya, `Ektarya of Agricultural Product ${index + 1}`),
            bilangPuno: validateInteger(tempId, null, item.BilangPuno, `Bilang Puno of Agricultural Products ${index + 1}`),
            punoAniUnit: validateString(tempId, null, bilangPunoAniUnit, item.BilangPunoUnit, `Bilang Puno Unit of Agricultural Products ${index + 1}`),
            barangay: barangayId || '',
            municipality: cityMunicipalityId || '',
            province: provinceId || '',
            unit: validateString(tempId, null, areaUnits, item.Unit, `Unit of Agricultural Products ${index + 1}`) || ''
          })
        })),
        assistanceSource: validateValue(tempId, null, assistanceSource, drrm.AssistanceSource, 'Assistance Source') || '',
        assistanceType: validateValue(tempId, null, assistanceType, drrm.AssistanceType, 'Assistance Type') || '',
        animalsRaised: (animalsMap[tempId] || []).map((item, index) => ({
          animal: validateValue(tempId, null,  animalsForFood, item.Uri, `Uri of Animals Raised ${index + 1}`) || '',
          bilang: item.Bilang ? parseInt(item.Bilang) : null
        })),
        barangay: barangayId || '',
        birthPlace: head.BirthPlace || '',
        birthdate: formatDate(head.Birthdate),
        boatEntries: (boatsMap[tempId] || []).map((item, index) => ({
          vesselType: validateValue(tempId, null, vesselTypeOptions, item.Uri, `Vessel Type of Boat Entries ${index + 1}`) || '',
          vesselQuantity: validateInteger(tempId, null, item.Bilang, `Vessel Quantity of Boat Entries ${index + 1}`),
          commercialCount: validateInteger(tempId, null, item.CommercialCount, `Commercial Count of Boat Entries ${index + 1}`),
          municipalCount: validateInteger(tempId, null, item.MunicipalCount, `Municipal Count of Boat Entries ${index + 1}`),
          materialUsed: validateValue(tempId, null, materialUsedOptions, item.MaterialUsed, `Material Used of Boat Entries ${index + 1}`) || ''
        })),
        citizenship: head.Citizenship || 'Philippines',
        cityMunicipality: cityMunicipalityId || '',
        civilStatus: validateString(tempId, null, civilStatusOptions, head.CivilStatus, 'Civil Status') || '',
        contactNumber: [
          contact?.ContactNumber1 || '',
          contact?.ContactNumber2 || '',
          contact?.ContactNumber3 || ''
        ].filter(n => n),
        cookingFac: validateValue(tempId, null, cookingFacOptions, hc.CookingFacility, 'Cooking Facility') || '',
        cookingFacOther: "",
        cookingFacType: "",
        cultivatedLand: await Promise.all((agriLandMap[tempId] || []).map(async (item, index) => {
          const provinceId = await validateProvince((item.Probinsya || '').toUpperCase(), 'Province of Cultivated Land', tempId, null);
          const cityMunicipalityId = provinceId ? await validateCityMunicipality((item.Munisipyo || '').toUpperCase(), provinceId, 'Municipality of Cultivated Land', tempId, null) : null;
          const barangayId = cityMunicipalityId ? await validateBarangay((item.Barangay || '').toUpperCase(), cityMunicipalityId, 'Barangay of Cultivated Land', tempId, null) : null
          return ({
            lawak: item.Lawak ? parseFloat(item.Lawak) : null,
            lawakUnit: validateString(tempId, null, areaUnits, item.LawakUnit, `Lawak Unit of Cultivated Land ${index + 1}`) || 'hectares',
            barangay: barangayId || '',
            municipality: cityMunicipalityId || '',
            province: provinceId || '',
            kalagayan: validateValue(tempId, null, landOwnershipOptions, item.Kalagayan, `Kalagayan of Cultivated Land ${index + 1}`) || '',
            mayAri: item.MayAri || ''
          })
        })),
        disasterImpact: validateValue(tempId, null, disasterImpactOptions, drrm.DisasterImpact, 'Disaster Impact') || '',
        disasterType: validateValue(tempId, null, disasterTypeOptions, drrm.DisasterType, 'Disaster Type') || '',
        ecologicalSetting: validateValue(tempId, null, ecologicalSettingOptions, drrm.EcologicalSetting, 'Ecological Setting') || '',
        environmentSanitation: {
          selectedWaterSource: (splitCommaSeparated(env?.SelectedGeneralWaterSource) || []).map((item, index) => {
            return validateString(tempId, null, waterSourceOptions, item, `General Water Source ${index}`);
          }),
          selectedWaterLocation: validateString(tempId, null, waterLocations, env.GeneralWaterLocation, 'General Water Location') || '',
          isWaterAvailable: validateString(tempId, null, waterAvailabilityOptions, env?.IsGeneralWaterAvailable, 'General Water Availability') || '',
          selectedWaterSource2: (splitCommaSeparated(env?.SelectedDrinkingWaterSource) || []).map((item, index) => {
            return validateString(tempId, null, waterSourceOptions, item, `Drinking Water Source ${index}`);
          }),
          selectedWaterLocation2: validateString(tempId, null, waterLocations, env.DrinkingWaterLocation, 'Drinking Water Location') || '',
          isWaterAvailable2: validateString(tempId, null, waterAvailabilityOptions, env.IsDrinkingWaterAvailable, 'Drinking Water Availability') || '',
          selectedToiletFacility: (splitCommaSeparated(env?.SelectedToiletFacility) || []).map((item, index) => {
            return validateString(tempId, null, toiletFacilities, item, `Toilet Facility ${index}`);
          }),
          selectedToiletLocation: validateString(tempId, null, waterLocations, env.ToiletLocation, 'Toilet Location') || '',
          toiletSharing: validateString(tempId, null, toiletSharingOptions, env.ToiletSharing, 'Toilet Sharing') || '',
          selectedGarbageDisposal: (splitCommaSeparated(env?.SelectedGarbageDisposal) || []).map((item, index) => {
            return validateString(tempId, null, garbageDisposalOptions, item, `Garbage Disposal ${index}`)
          })
        },
        evacuationArea: validateValue(tempId, null, evacuationAreaOptions, drrm.EvacuationArea, 'Evacuation Area') || '',
        experiencedDisaster: validateString(tempId, null, experiencedDisasterOptions, drrm.ExperiencedDisaster, 'Experienced Disaster') || '',
        externalHouseWall: validateValue(tempId, null, externalHouseWallOptions, hc.ExternalHouseWall, 'External House Wall'),
        externalHouseWallOther: "",
        externalHouseWallType: "",
        familyMembers: await Promise.all((membersMap[tempId] || []).map(async member => {
          const tempMemberID = member.TempMemberID;
          const mhn_member = mhn.find(hn => hn.MemberID === tempMemberID);
          if (mhn_member === undefined) {
            setValidationErrors((prev) => [...prev, `${tempId} has a family member without MemberID`]);
            return {};
          }
          const G17Data = {
            primary:  '',
            secondary: '',
            ...(mhn_member.BCG && mhn_member.BCG === 1 ? {BCG: {received: true}} : {}),
            ...(mhn_member.HepatitisB && mhn_member.HepatitisB === 1 ? {HepatitisB: {received: true}} : {}),
            ...(mhn_member.IPV && mhn_member.IPV === 1 ? {IPV: {received: true}} : {}),
            ...(mhn_member.MMR && mhn_member.MMR === 1 ? {MMR: {received: true}} : {}),
            ...(mhn_member.OPV && mhn_member.OPV === 1 ? {OPV: {received: true}} : {}),
            ...(mhn_member.Pentavalent && mhn_member.Pentavalent === 1 ? {Pentavalent: {received: true}} : {}),
            ...(mhn_member.PCV && mhn_member.PCV === 1 ? {Pneumococcal: {received: true}} : {}),
            ...(mhn_member.OtherVaccines && mhn_member.OtherVaccines === 1 ? {Other: {received: true}} : {})
          };          
          const G18Data = {
            status: mhn_member.DewormingStatus || '',
            date: mhn_member.DewormedDate || null
          };
          const G19Data = {
            Iron: {...(mhn_member.Iron && mhn_member.Iron === 1 ? {received: true} : {}), date: null},
            VitaminA: {...(mhn_member.VitaminA && mhn_member.VitaminA === 1 ? {received: true} : {}), date: null},
            ...(mhn_member.IodizedSalt && mhn_member.IodizedSalt === 1 ? {IodizedSalt: {received: true}} : {}),
            ...(mhn_member.IFR && mhn_member.IFR === 1 ? {IronFortifiedRice: {received: true}} : {}),
            ...(mhn_member.MNP && mhn_member.MNP === 1 ? {MNP: {received: true}} : {}),
            date: "",
            status: ""
          };
          const G20Data = {
            DateLastBreastfed: formatDate(mhn_member.BreastFeedingDate) || null,
            ...(mhn_member.EBF_1 && mhn_member.EBF === 1 ? {Month1: true} : {}),
            ...(mhn_member.EBF_2 && mhn_member.EBF === 1 ? {Month2: true} : {}),
            ...(mhn_member.EBF_3 && mhn_member.EBF === 1 ? {Month3: true} : {}),
            ...(mhn_member.EBF_4 && mhn_member.EBF === 1 ? {Month4: true} : {}),
            ...(mhn_member.EBF_5 && mhn_member.EBF === 1 ? {Month5: true} : {}),
            ...(mhn_member.EBF_6 && mhn_member.EBF === 1 ? {Month6: true} : {}),
            primary: "",
            secondary: ""
          };
          const G21Data = validateString(tempId, tempMemberID, feedingStatusOptions, mhn_member.FeedingStatus, 'Feeding Status') || null;
          const provinceId = await validateProvince((member.BirthProvince || '').toUpperCase(), 'Birth Province', tempId, tempMemberID);
          const cityMunicipalityId = provinceId ? await validateCityMunicipality((member.BirthMunicipality || '').toUpperCase(), provinceId, 'Birth Municipality', tempId, tempMemberID) : null;
          const barangayId = cityMunicipalityId ? await validateBarangay((member.BirthBarangay || '').toUpperCase(), cityMunicipalityId, 'Birth Barangay', tempId, tempMemberID) : null;
          
          const validatedIsOFW = validateString(tempId, tempMemberID, isOFWOptions, member.IsOFW, 'Is OFW') || null
          const validatedSeekingWorkValue = validateString(tempId, tempMemberID, seekingWorkOptions, member.SeekingWork, 'Seeking Work') || null;
          const validatedWithSeniorIdValue = validateString(tempId, tempMemberID, withSeniorIdOptions, member.WithSeniorID, 'With Senior ID') || null;
          const seekingWorkValue = validatedSeekingWorkValue === "1 - Actively seeking" ? 1 : (validatedSeekingWorkValue === "2 - Not actively seeking" ? 0 : null);
          const withSeniorIdValue = validatedWithSeniorIdValue === "1 Yes" ? 1 : (validatedWithSeniorIdValue === "2 No" ? 0 : null);
          const isOfwValue = validatedIsOFW === "1 - Yes" ? 1 : (validatedIsOFW === "2 - No" ? 0 : null);
          return ({
            birthBarangay: barangayId || '',
            birthMunicipality: cityMunicipalityId || '',
            birthPlace: member.BirthPlace || '',
            birthProvince: provinceId || '',
            birthdate: formatDate(member.Birthdate),
            breastfeedingDate: formatDate(mhn_member.BreastFeedingDate) || '',
            breastfeedingStatus: mhn_member.BreastfeedingStatus || '',
            causeOfDeath: member.CauseOfDeath || '',
            citizenship: member.Citizenship || 'Philippines',
            civilStatus: validateString(tempId, tempMemberID, civilStatusOptions, member.CivilStatus, 'Civil Status') || '',
            classOfWorker: validateString(tempId, tempMemberID, classOfWorkerOptions, member.ClassOfWorker, 'Class of Worker') || '',
            contraceptivesUsed: validateString(tempId, tempMemberID, contraceptiveOptions, mhn_member.ContraceptivesUsed, 'Contraceptive  Used') || '',
            countryOfWork: member.CountryOfWork || '',
            degree: member.Degree || '',
            dewormingDate: formatDate(mhn_member.DewormedDate) || '',
            dewormingStatus: validateString(tempId, tempMemberID, dewormingStatusOptions, mhn_member.DewormingStatus, 'Deworming Status') || '',
            disability: mhn_member.Disability || '',
            disease: mhn_member.Disease || '',
            educationalAttainment: splitCommaSeparated(member.EducationalAttainment).map((item, index) => validateString(tempId, tempMemberID, educationalAttainmentOptions, item, `Educational Attainment ${index}`)),
            ethnicity: member.Ethnicity || '',
            everPregnant: validateString(tempId, tempMemberID, isEverPregnantOptions, mhn_member.EverPregnant, 'Is Ever Pregnant') || '',
            firstName: (member.FirstName || '').toUpperCase() || '',
            gender: validateString(tempId, tempMemberID, genderOptions, member.Gender, 'Gender') || '',
            healthInsurance: validateString(tempId, tempMemberID, healthInsuranceOptions, mhn_member.HealthInsurance, 'Health Insurance') || '',
            id: null,
            ifaIntake: validateString(tempId, tempMemberID, ifaIntakeOptions, mhn_member.IFAIntake, 'IFA Intake')|| '',
            ifaSource: validateString(tempId, tempMemberID, ifaSourceOptions, mhn_member.IFASource, 'IFA Source') || '',
            isOFW: isOfwValue,
            lactationStatus: validateString(tempId, tempMemberID, lactationStatusOptions, mhn_member.LactationStatus, 'Lactation Status') || '',
            lastName: (member.LastName || '').toUpperCase() || '',
            mainIncomeSource: member.MainIncomeSource || '',
            micronutrientDate: formatDate(mhn_member.MicronutrientDate) || '',
            micronutrientStatus: mhn_member.MicronutrientStatus || '',
            middleName: (member.MiddleName || '').toUpperCase() || '',
            migrationStatus: validateString(tempId, tempMemberID, migrationStatusOptions, member.MigrationStatus, 'Migration Status') || '',
            monthlyIncome: validateFloat(tempId, tempMemberID, member.MonthlyIncome, 'Monthly Income'),
            organizations: member.Organizations || '',
            otherIncomeSource: member.OtherIncomeSource || '',
            pregnancyStatus: validateString(tempId, tempMemberID, pregnancyStatusOptions, mhn_member.PregnancyStatus, 'Pregnancy Status') || '',
            prenatalCheckup: validateString(tempId, tempMemberID, prenatalCheckupOptions, mhn_member.PrenatalCheckup, 'Prenatal Checkup') || '',
            pwdId: mhn_member.PWDId || '',
            registeredAtCivilRegistrar: validateString(tempId, tempMemberID, civilRegisteredOptions, member.CivilRegistered, 'Civil Registered') || '',
            registeredComelecVoter: validateString(tempId, tempMemberID, registeredVoterOptions, member.RegisteredVoter, 'Registered Voter') || '',
            relationship: validateString(tempId, tempMemberID, relationshipOptions, member.Relationship, 'Relationship') || '',
            religion: member.Religion || '',
            schoolName: member.SchoolName || '',
            schoolingReasons: splitCommaSeparated(member.SchoolingReasons).map((reason, index) => validateString(tempId, tempMemberID, schoolingReasonsOptions, reason, `Schooling Reason ${index}`)),
            schoolingStatus: validateString(tempId, tempMemberID, schoolingStatusOptions, member.SchoolingStatus, 'Schooling Status') || '',
            seekingWork: seekingWorkValue,
            seniorCitizenId: member.SeniorCitizenID || '',
            sex: validateString(tempId, tempMemberID, sexOptions, member.Sex, 'Sex') || '',
            skills: (splitCommaSeparated(member.Skills) || []).map((item, index) => {
              return item
            }),
            soloParentId: validateString(tempId, tempMemberID, soloParentOptions, member.SoloParentId, 'Solo Parent ID') || '',
            soloParentIdNo: member.SoloParentIdNo || '',
            status: validateStatus(member.Status, tempId, tempMemberID) || 'A - Active',
            suffix: (member.Suffix || '').toUpperCase() || '',
            totalLivingChildren: validateInteger(tempId, tempMemberID, mhn_member.TotalLivingChildren, 'Total Living Children'),
            totalMiscarriages: validateInteger(tempId, tempMemberID, mhn_member.TotalMiscarriages, 'Total Miscarriages'),
            totalPregnancies: validateInteger(tempId, tempMemberID, mhn_member.TotalPregnancies, 'Total Pregnancies'),
            totalStillbirths: validateInteger(tempId, tempMemberID, mhn_member.TotalStillBirths, 'Total Still Births'),
            vaccineInfo: mhn_member.VaccinesReceived || '',
            vaccinesReceivedPrimary: "",
            vaccinesReceivedSecondary: "",
            with_senior_id: withSeniorIdValue,
            G17: G17Data,
            G18: G18Data,
            G19: G19Data,
            G20: G20Data,
            G21: G21Data
          })
        })),
        familyType: validateString(tempId, null, familyTypeOptions, head.FamilyType, 'Family Type') || 'Main Family (Owner)',
        firstName: (head.FirstName || '').toUpperCase(),
        fisheriesEntries: await Promise.all((fisheriesMap[tempId] || []).map(async (item, index) => {
          const provinceId = await validateProvince((item.Probinsya || '').toUpperCase(), 'Province of Fisheries', tempId, null);
          const cityMunicipalityId = provinceId ? await validateCityMunicipality((item.Munisipyo || '').toUpperCase(), provinceId, 'Municipality of Fisheries', tempId, null) : null;
          const barangayId = cityMunicipalityId ? await validateBarangay((item.Barangay || '').toUpperCase(), cityMunicipalityId, 'Barangay of Fisheries', tempId, null) : null
          return ({
            barangay: barangayId || '',
            fishpondType: validateValue(tempId, null, fishpondTypeOptions, item.FishpondType, `Fishpond Type ${index + 1}`) || '',
            kalagayan: validateValue(tempId, null, fishpondOwnershipOptions, item.Kalagayan, `Kalagayan of Fishery Entry ${index + 1}`) || '',
            municipality: cityMunicipalityId || '',
            province: provinceId || '',
            quantity: item.Bilang ? parseInt(item.Bilang) : null,
            size: validateFloat(tempId, null, item.Sukat, `Size of Fishery Entries ${index}`) || '',
            units: validateFloat(tempId, null, item.Unit, `Units of Fishery Entries ${index}`) || '',
            uri: validateValue(tempId, null, fisheriesOptions, item.Isda, `Fish Uri ${index + 1}`) || '',
            waterType: validateValue(tempId, null, waterTypeOptions, item.TubigUri, `Water Type ${index + 1}`) || '',
          })
        })),
        fishingGearEntries: {
          'Falling Gear': FallingGear.reduce((acc, gear) => {
            const quantity = validateQuantity(tempId, gears, gear);
            if (quantity >= 1) acc[gear] = quantity;
            return acc;
          }, {}),
          'Gill Nets': GillNets.reduce((acc, gear) => {
            const quantity = validateQuantity(tempId, gears, gear);
            if (quantity >= 1) acc[gear] = quantity;
            return acc;
          }, {}),
          'Hook and Line': HooksAndLine.reduce((acc, gear) => {
            const quantity = validateQuantity(tempId, gears, gear);
            if (quantity >= 1) acc[gear] = quantity;
            return acc;
          }, {}),
          'Lift Nets': LiftNets.reduce((acc, gear) => {
            const quantity = validateQuantity(tempId, gears, gear);
            if (quantity >= 1) acc[gear] = quantity;
            return acc;
          }, {}),
          'Miscellaneous Fishing Gear': MiscellaneousFishingGear.reduce((acc, gear) => {
            const quantity = validateQuantity(tempId, gears, gear);
            if (quantity >= 1) acc[gear] = quantity;
            return acc;
          }, {}),
          'Pots and Traps': PotsAndTraps.reduce((acc, gear) => {
            const quantity = validateQuantity(tempId, gears, gear);
            if (quantity >= 1) acc[gear] = quantity;
            return acc;
          }, {}),
          'Seine Nets': SeineNets.reduce((acc, gear) => {
            const quantity = validateQuantity(tempId, gears, gear);
            if (quantity >= 1) acc[gear] = quantity;
            return acc;
          }, {}),
        },
        floorMat: validateValue(tempId, null, floorMatOptions, hc.FlooringMaterial, 'Flooring Material') || '',
        floorMatOther: "",
        floorMatType: "",
        foundationMaterial: validateValue(tempId, null, foundationMaterialOptions, drrm.FoundationMaterial, 'Foundation Material') || '',
        foundationMaterialOther: "",
        gableWallMaterial: validateValue(tempId, null, gableWallMaterialOptions, drrm.GabbleWallMaterial, "Gabble Wall Material") || '',
        gableWallMaterialOther: "",
        gender: head.Gender || '',
        headID: null,
        houseAffectedByDisaster: validateValue(tempId, null, houseAffectedByDisasterOptions, drrm.HouseAffectedByDisaster, 'House Affected By Disaster') || '',
        houseBuiltBefore1990: validateValue(tempId, null, houseBuiltBefore1990Options, drrm.HouseBuiltBefore1990, 'House Built Before 1990') || '',
        houseCondition: validateValue(tempId, null, houseConditionOptions, drrm.HouseCondition, 'House Condition') || '',
        houseConditionOther: "",
        houseExtension: validateValue(tempId, null, houseExtensionOptions, drrm.HouseExtension, 'House Extension') || '',
        houseExtensionOther: "",
        houseLocation: validateValue(tempId, null, houseLocationOptions, drrm.HouseLocation, 'House Location') || '',
        houseLocationOther: "",
        houseNo: head.HouseNo || '',
        houseOwnership: validateValue(tempId, null, ownershipOptions, hc.HouseOwnership, 'House Ownership') || '', 
        houseOwnershipOther: hc.HouseOwnershipOther || '',
        houseOwnershipType: hc.HouseOwnershipType || '',
        houseShape: validateValue(tempId, null, houseShapeOptions, drrm.HouseShape, 'House Shape') || '',
        houseStructure: validateValue(tempId, null, houseStructureOptions, hc.HouseStructure, 'House Structure') || '',
        houseStructureOther: hc.HouseStructureOther || '',
        houseStructureType: hc.HouseStructureType || '',
        householdAppliances: buildAssetObject(applianceItems, assets),
        householdAssets: [
          ...applianceItems.map((item) => {
            return validateAssetQuantity(tempId, assets, 'Appliance', item)
          }).filter(item => item !== undefined),
          ...ictDeviceItems.map((item) => {
            return validateAssetQuantity(tempId, assets, 'ICT', item);
          }).filter(item => item !== undefined),
          ...applianceItems.map((item) => {
            return validateAssetQuantity(tempId, assets, 'Vehicle', item);
          }).filter(item => item !== undefined)
        ],
        householdType: validateString(tempId, null, householdTypes, head.HouseholdType, 'Household Type') || '',
        ictDevices: buildAssetObject(ictDeviceItems, assets),
        internetProvider: validateString(tempId, null, internetProviderOptions, head.InternetProvider, 'Internet Provider') || '',
        internetProviderOther: head.InternetProviderOther || '',
        lastName: (head.LastName || '').toUpperCase() || '',
        lastPageData: {
          foodSecurity: lastPage ? {
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H1, 'FoodSecurity_H1') ? {H1: lastPage?.FoodSecurity_H1} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H2, 'FoodSecurity_H2') ? {H2: lastPage?.FoodSecurity_H2} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H3, 'FoodSecurity_H3') ? {H3: lastPage?.FoodSecurity_H3} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H4, 'FoodSecurity_H4') ? {H4: lastPage?.FoodSecurity_H4} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H5, 'FoodSecurity_H5') ? {H5: lastPage?.FoodSecurity_H5} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H6, 'FoodSecurity_H6') ? {H6: lastPage?.FoodSecurity_H6} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H7, 'FoodSecurity_H7') ? {H7: lastPage?.FoodSecurity_H7} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H8, 'FoodSecurity_H6') ? {H8: lastPage?.FoodSecurity_H8} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H9, 'FoodSecurity_H9') ? {H9: lastPage?.FoodSecurity_H9} : {}),
            ...(lastPage?.FoodSecurity_H10 ? {H10: lastPage?.FoodSecurity_H10} : {}),
            ...(isValidYesNoInput(tempId, lastPage?.FoodSecurity_H11, 'FoodSecurity_H11') ? {H11: lastPage?.FoodSecurity_H11} : {}),
            ...(lastPage?.FoodSecurity_H12 ? {H12: lastPage?.FoodSecurity_H12} : {}),
          } : {},
          socialProtection: {
            I1: splitCommaSeparated(lastPage?.SocialProtection_I1).map((item, index) => validateString(tempId, null, socialProtectionOptions, item, `Social Protection I1 - ${index+1}`)),
            I2: splitCommaSeparated(lastPage?.SocialProtection_I2).map((item, index) => validateString(tempId, null, socialProtectionOptions, item, `Social Protection I2 - ${index+1}`))
          },
          peaceAndOrder: {
            J1: isValidYesNoInput(tempId, lastPage?.PeaceAndOrder_J1, 'PeaceAndOrder_J1') || '',
            J2: lastPage?.PeaceAndOrder_J2 || '',
            J3: J3LocationCityMunicipality || '',
            J4: validateString(tempId, null, [{label: 'Ligtas'}, {label: 'Hindi Ligtas'}], lastPage?.PeaceAndOrder_J4, 'PeaceAndOrder_J1') || ''
          }
        },
        latitude: validateFloat(tempId, null, head.Latitude, 'Latitude'),
        longitude: validateFloat(tempId, null, head.Longitude, 'Longitude'),
        lotOwnership: validateValue(tempId, null, ownershipOptions, hc.LotOwnership, 'Lot Ownership') || '',
        lotOwnershipOther: hc.LotOwnershipOther || '',
        middleName: (head.MiddleName || '').toUpperCase() || '',
        mobileProvider: [
          validateString(tempId, null, mobileProviderOptions, contact?.MobileProvider1, 'Mobile Provider 1') || '',
          validateString(tempId, null, mobileProviderOptions, contact?.MobileProvider2, 'Mobile Provider 2') || '',
          validateString(tempId, null, mobileProviderOptions, contact?.MobileProvider3, 'Mobile Provider 3') || ''
        ].filter(p => p),
        municipality: head.Municipality || '',
        nipaEntries: await Promise.all((nipaMap[tempId] || []).map(async (item, index) => {
          const provinceId = await validateProvince((item.Probinsya || '').toUpperCase(), 'Province of Nipa Production', tempId, null);
          const cityMunicipalityId = provinceId ? await validateCityMunicipality((item.Munisipyo || '').toUpperCase(), provinceId, 'Municipality of Nipa Production', tempId, null) : null;
          const barangayId = cityMunicipalityId ? await validateBarangay((item.Barangay || '').toUpperCase(), cityMunicipalityId, 'Barangay of Nipa Production', tempId, null) : null
          return ({
            product: item.Uri || '',
            volume: validateInteger(tempId, null, item.Bilang, `Nipa Entries ${index}`),
            barangay: barangayId || '',
            municipality: cityMunicipalityId || '',
            province: provinceId || ''
          })
        })),
        numFamilyMembers: validateInteger(tempId, null, head.NumberOfFamilyMembers, 'Number of Family Members') || null,
        petAnimals: (petsMap[tempId] || []).map((item, index) => ({
          animal: validateValue(tempId, null, animalsForPets, item.Uri, 'Uri of Pet Animal') || '',
          bilang: validateInteger(tempId, null, item.Bilang, `Bilang of Pet Animals ${index}`)
        })),
        prevLocation: prevLocationCityMunicipality || null,
        province: provinceId || '',
        purokSitio: head.PurokSitio || '',
        roofingMat: validateValue(tempId, null, roofingMatOptions, hc.RoofingMaterial, 'Roofing Material') || '',
        roofingMatOther: "",
        roofingMatType: "",
        senderId: localStorage.getItem('userId'),
        sex: validateString(tempId, null, sexOptions, head.Sex, 'Sex') || '',
        soilCondition: validateValue(tempId, null, soilConditionOptions, drrm.SoilCondition, 'Soil Condition') || '',
        soilConditionOther: "",
        sourceElect: validateValue(tempId, null, sourceElectOptions, hc.SourceElectricity, 'Source Electricity') || '',
        sourceElectOther: "",
        sourceElectType: "",
        status: validateStatus(head.Status, tempId, null) || 'A - Active',
        steelStandard: validateValue(tempId, null, steelStandardOptions, drrm.SteelStandard, 'Steel Standard') || '',
        steelStandardOther: "",
        street: head.Street || '',
        suffix: (head.Suffix || '').toUpperCase() || '',
        vehicles: buildAssetObject(vehicleItems, assets),
        wallSupport: validateValue(tempId, null, wallSupportOptions, drrm.WallSupport, 'Wall Support') || '',
        wallSupportOther: "",
        wallThickness: validateValue(tempId, null, wallThicknessOptions, drrm.WallThickness, 'Wall Thickness') || '',
        wallThicknessOther: "",
        yearMoved: validateInteger(tempId, null, head.YearMoved, 'Year Moved'),
        };
      }));
      return results;
    // } catch (error) {
    //   alert('An error occured. Please contact the administrator or double check the file contents.');
    //   return [];
    // }
  };

  const handleSubmit = async () => {
    if (!jsonData) {
      alert("No Excel data to upload. Please choose a file first.");
      return;
    }

    const payloads = await transformToPayloads(jsonData);
    // console.log(validationErrors)
    if (!payloads.length) {
      alert("No valid payloads to upload.");
      return;
    }

    setIsSubmitting(true);
    try {
      for (const payload of payloads) {
        // You may want to batch upload, but for now, upload one by one
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload/mass-upload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY
          },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Upload failed');
      }
      // alert('Upload successful!');
      setPopup({ show: true, type: "success", message: "Data submitted successfully!" });
      setUploadedFile(null);
      setJsonData(null);
    } catch (error) {
      console.error('Upload error:', error);
      // alert(`Upload failed: ${error.message}`);
      setPopup({ show: true, type: "error", message: "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const result = {};
      workbook.SheetNames.forEach((sheet) => {
        result[sheet] = XLSX.utils.sheet_to_json(workbook.Sheets[sheet]);
      });
      setJsonData(result);
      console.log("Parsed Excel Data:", result);
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div style={{ padding: '2rem' }}>
      {isSubmitting && <LoadingOverlay />}
      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ show: false, type: "", message: "" })}
        />
      )}
      <h2>BMIS Mass Upload Manager</h2>

      <Button design="Emphasized" onClick={handleDownloadTemplate}>
        Download Excel Template
      </Button>

      <br /><br />

      <input type="file" accept=".xlsx" onChange={handleFileChange} />

      {uploadedFile && (
        <p style={{ marginTop: '1rem' }}>Selected file: <strong>{uploadedFile.name}</strong></p>
      )}

      {jsonData && (
        <>
          <pre style={{ maxHeight: '300px', overflow: 'auto', background: '#f3f3f3', padding: '1rem', marginTop: '1rem' }}>
            {JSON.stringify(jsonData, null, 2)}
          </pre>
          {/* Ensure the upload button is always visible when jsonData is present */}
          {validationErrors.length === 0 ? <Button design="Positive" onClick={handleSubmit} disabled={isSubmitting} style={{ marginTop: '1rem' }}>
            {isSubmitting ? 'Uploading...' : 'Upload File'} 
          </Button> : 
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "10px" }}>
            {validationErrors.map((error, index) => (
              <span
                key={index}
                style={{
                  backgroundColor: "rgba(249, 74, 30, 0.1)",
                  color: "#B00020",
                  border: "1px solid rgba(249, 74, 30, 0.5)",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                }}
              >
                ⚠️ {error}
              </span>
            ))}
          </div>
          }
        </>
      )} 
    </div>
  );
};

export default MassUploadManager;
